import { findPage, flattenPage, photoSize, type Quad } from '@core/files';

/**
 * Снятый кадр на пути к странице документа.
 *
 * Между съёмкой и документом стоит один экран — правка границ. Всё, что нужно
 * этому экрану, собрано здесь: сам кадр, найденные углы и способ его выпрямить.
 */
export interface Shot {
  photo: File;
  corners: Quad;
  /** Нашли ли лист сами. Нет — углы стоят по краю кадра, и правка обязательна. */
  detected: boolean;
  /** Выпрямляет кадр по правленым углам и отдаёт готовую страницу. */
  flatten: (corners: Quad) => Promise<File>;
}

/**
 * Готовит снятый кадр к правке границ.
 *
 * **Отказ обработки не теряет страницу.** OpenCV не загрузился, кадр не разобрался —
 * снимок всё равно попадёт в дело как есть: несделанный документ хуже неровного.
 * Углы тогда стоят по краю кадра, и юрист расставит их сам.
 *
 * **Край кадра берётся из самого снимка.** Раньше при отказе сюда ставился квадрат
 * в один пиксель — углы в координатах снимка, а не в долях. На экране правки все
 * четыре ручки слипались в точку в левом верхнем углу, и выглядело это поломкой:
 * растащить рамку по листу можно, но догадаться, что её надо тащить, — нет.
 */
export async function processShot(photo: File): Promise<Shot> {
  const found = await findPage(photo).catch(() => null);
  const size = found ? null : await photoSize(photo).catch(() => null);

  const corners: Quad = found?.corners ??
    frameOf(size?.width ?? 1, size?.height ?? 1);

  return {
    photo,
    corners,
    detected: found?.detected ?? false,

    async flatten(edited: Quad): Promise<File> {
      const flattened = await flattenPage(photo, edited).catch(() => null);
      if (!flattened) return photo;

      return new File([flattened], photo.name || 'page.jpg', { type: 'image/jpeg' });
    },
  };
}

/** Рамка по краю кадра — в координатах самого снимка, как их ждёт экран правки. */
const frameOf = (width: number, height: number): Quad => [
  { x: 0, y: 0 },
  { x: width, y: 0 },
  { x: width, y: height },
  { x: 0, y: height },
];
